import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

// Camera-only capture with GPS + datetime watermark burned into the image.
export const CameraCapture = ({ open, onClose, onCapture, label }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [gps, setGps] = useState("");
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReady(false);
    navigator.geolocation?.getCurrentPosition(
      (p) => setGps(`${p.coords.latitude.toFixed(5)}, ${p.coords.longitude.toFixed(5)}`),
      () => setGps("GPS non disponibile"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); setReady(true); }
      } catch (e) {
        toast.error("Fotocamera non disponibile. Consenti l'accesso alla camera.");
      }
    })();
    return () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };
  }, [open]);

  const shoot = async () => {
    const video = videoRef.current, canvas = canvasRef.current;
    if (!video || !canvas) return;
    setBusy(true);
    const w = video.videoWidth || 1280, h = video.videoHeight || 720;
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    const now = new Date();
    const ts = now.toISOString();
    const lines = [`${now.toLocaleString("it-IT")}`, `GPS: ${gps || "n/d"}`, label ? `Foto: ${label}` : ""].filter(Boolean);
    const pad = Math.round(w * 0.015), fs = Math.max(16, Math.round(w * 0.028));
    ctx.font = `600 ${fs}px sans-serif`;
    const boxH = lines.length * (fs + 6) + pad * 2;
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(0, h - boxH, w, boxH);
    ctx.fillStyle = "#fff";
    ctx.textBaseline = "top";
    lines.forEach((ln, i) => ctx.fillText(ln, pad, h - boxH + pad + i * (fs + 6)));
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    try {
      const fd = new FormData();
      fd.append("file", blob, "checkin.jpg");
      const r = await api.post("/uploads/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onCapture({ label, path: r.data.path, gps, ts });
      streamRef.current?.getTracks().forEach((t) => t.stop());
      onClose();
    } catch (e) {
      toast.error("Caricamento foto non riuscito");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="camera-capture-dialog">
        <DialogHeader><DialogTitle className="font-heading">Scatta: {label}</DialogTitle></DialogHeader>
        <div className="relative overflow-hidden rounded-lg bg-black">
          <video ref={videoRef} playsInline muted className="w-full" style={{ maxHeight: "60vh" }} />
          {!ready && <div className="absolute inset-0 flex items-center justify-center text-white"><Loader2 className="h-6 w-6 animate-spin" /></div>}
          <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1 text-[11px] text-white">
            {new Date().toLocaleString("it-IT")} · GPS: {gps || "…"}
          </div>
        </div>
        <canvas ref={canvasRef} className="hidden" />
        <div className="flex justify-between gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-md"><RotateCcw className="mr-2 h-4 w-4" />Annulla</Button>
          <Button onClick={shoot} disabled={!ready || busy} data-testid="camera-shoot-btn" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />}Scatta
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
